import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../core/utils/hook';
import { Table, TableSkeleton } from '../../components';
import { Link, useNavigate } from 'react-router-dom';
import NotifyList from './components/NotifyList';
import { flowResult } from 'mobx';
import { SYSTEM_PATH } from '../../core/configs/constants';
import VerticalCarousel from '../../components/CarouselSwiper/VerticalCarousel';
import FirstTest from './components/FirstTest';
import classNames from 'classnames';
import moment from 'moment-timezone';
import './style.scss';

const GAHomeScreen = observer(() => {
    // other
    const navigate = useNavigate();
    const [selectedAttendanceRows, setSelectedAttendanceRows] = useState([]);
    const [selectedCompleteRateRows, setSelectedCompleteRateRows] = useState([]);

    // store
    const { 
        groupAdminStore: { 
            attendancesOfCompany, companyAttendanceRateList, selectedGroupIds, gaFilterMode,
            loadingAttendancesOfCompany, loadingCompanyAttendanceRate,
            getAttendancesOfCompany, getCompanyAttendanceRate, exportAttendanceStudentListCsv, exportAttendanceCompleteRateCsv
        },
        userStore: {
            userInfoHeader
        },
        informationStore: { lstInformation, getAllInformationsForStudent, viewInformation }
    } = useStore();

    // memo
    const formatDataCarouselInformation = useMemo(() => {
        if(!lstInformation || lstInformation?.length < 0) return [];
        return lstInformation.map(item => ({
            id: item.id,
            title: item.title,
            url: `${SYSTEM_PATH.INFORMATION_SCREEN}/${item.id}`,
            publicDate: item.publicDate
        }))
    }, [lstInformation])

    const isDepartmentFilter = useMemo(() => {
        if (userInfoHeader?.departmentId) {
            return true;
        }
        return !!userInfoHeader?.enableDepartmentHierarchy && gaFilterMode === 'department';
    }, [userInfoHeader?.departmentId, userInfoHeader?.enableDepartmentHierarchy, gaFilterMode]);

    // #17989 - the header refetches userInfoHeader (new object each time) and fills selectedGroupIds
    // a moment after login, so an effect keyed on those references fired 3 times and every home API
    // was requested 3 times. Key it on the values that actually change the requests instead, and
    // wait for the group filter when the header is about to populate it (a GA always has at least
    // its own company in childrenOfGroup, see GET /student/group/:id).
    const parentGroup = userInfoHeader?.groups?.length > 1;
    const groupFilterKey = JSON.stringify(selectedGroupIds || []);
    const waitingForGroupFilter = !isDepartmentFilter && !selectedGroupIds?.length && !!userInfoHeader?.groupId;

    const getCurrentGroupFilterPayload = () => ({
        groupIds: selectedGroupIds,
        parentGroup
    });

    // Do not call groupAdminStore.clean() here: it resets gaFilterMode and department filter state.
    useEffect(() => {
        if (!userInfoHeader?.id || isDepartmentFilter || waitingForGroupFilter) return;
        onFetchData();
    }, [userInfoHeader?.id, parentGroup, groupFilterKey, gaFilterMode, isDepartmentFilter, waitingForGroupFilter])

    // The information carousel does not depend on the group filter, so it must never wait for it
    // (e.g. when GET /student/group/:id fails the tables stay empty but the news still loads).
    useEffect(() => {
        if (!userInfoHeader?.id) return;
        getAllInformationsForStudent({ isHomepage: true });
    }, [userInfoHeader?.id])

    // function
    const onFetchData = () => {
        setSelectedAttendanceRows([]);
        setSelectedCompleteRateRows([]);
        onGetAttendancesOfCompany();
        onGetCompanyAttendanceRate();
    }

    const onGetAttendancesOfCompany = (data = {}) => {
        getAttendancesOfCompany({
            ...data,
            ...getCurrentGroupFilterPayload()
        });
    }

    const onGetCompanyAttendanceRate = (data = {}) => {
        getCompanyAttendanceRate({
            ...data,
            ...getCurrentGroupFilterPayload()
        });
    }

    const onHandleNavigateDetailSlide = async (id) => {
        const res = await flowResult(viewInformation(id, {}));
        if (res) return res;
        return null;
    }

    const selectedAttendanceRowIds = useMemo(() => {
        const obj = {};
        selectedAttendanceRows.forEach(item => {
            obj[item.id] = true;
        });
        return obj;
    }, [selectedAttendanceRows]);

    const onAttendanceSelectedChange = (selectedRows) => {
        const currentAttendances = attendancesOfCompany?.elements || [];
        if (!selectedRows) {
            setSelectedAttendanceRows([]);
            return;
        }

        const selectedIds = Object.keys(selectedRows);
        setSelectedAttendanceRows(oldRows => {
            let data = oldRows;
            currentAttendances.forEach(item => {
                const index = data.findIndex(e => e.id === item.id);
                if (selectedIds.includes(String(item.id))) {
                    if (index < 0) {
                        data = [...data, item];
                    }
                } else if (index > -1) {
                    data = [...data.slice(0, index), ...data.slice(index + 1)];
                }
            });
            return data;
        });
    };

    const onExportAttendanceStudentList = async () => {
        if (selectedAttendanceRows?.length > 0) {
            const attendanceIds = selectedAttendanceRows
                .map((item) => Number(item.id))
                .filter((id) => !Number.isNaN(id));
            if (!attendanceIds.length) return;
            await flowResult(exportAttendanceStudentListCsv({ attendanceIds }));
            return;
        }

        await flowResult(exportAttendanceStudentListCsv({
            exportAll: true,
            ...getCurrentGroupFilterPayload()
        }));
    };

    const selectedCompleteRateRowIds = useMemo(() => {
        const obj = {};
        selectedCompleteRateRows.forEach(item => {
            obj[item.groupAttendanceId] = true;
        });
        return obj;
    }, [selectedCompleteRateRows]);

    const onCompleteRateSelectedChange = (selectedRows) => {
        const currentAttendances = companyAttendanceRateList?.elements || [];
        if (!selectedRows) {
            setSelectedCompleteRateRows([]);
            return;
        }

        const selectedIds = Object.keys(selectedRows);
        setSelectedCompleteRateRows(oldRows => {
            let data = oldRows;
            currentAttendances.forEach(item => {
                const index = data.findIndex(e => e.groupAttendanceId === item.groupAttendanceId);
                if (selectedIds.includes(String(item.groupAttendanceId))) {
                    if (index < 0) {
                        data = [...data, item];
                    }
                } else if (index > -1) {
                    data = [...data.slice(0, index), ...data.slice(index + 1)];
                }
            });
            return data;
        });
    };

    const onExportAttendanceCompleteRate = async () => {
        if (selectedCompleteRateRows?.length > 0) {
            const attendanceRateItems = selectedCompleteRateRows
                .map((item) => ({
                    groupAttendanceId: Number(item.groupAttendanceId),
                    attendanceCode: item.attendanceCode ?? ''
                }))
                .filter((item) => !Number.isNaN(item.groupAttendanceId));
            if (!attendanceRateItems.length) return;
            await flowResult(exportAttendanceCompleteRateCsv({ attendanceRateItems }));
            return;
        }

        await flowResult(exportAttendanceCompleteRateCsv({
            exportAll: true,
            ...getCurrentGroupFilterPayload()
        }));
    };

    // table columns
    const attendanceColumns = useMemo(() => {
        const isParentGroup = userInfoHeader?.groups?.length > 1;
        // 承認待ち column only shows when the GA's group has the approval feature enabled.
        const isAllowApprove = userInfoHeader?.isAllowApproveAttendanceCode === true;

        const defaultColumns = [
            {
                Header: '受講コード',
                accessor: 'attendanceCode',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.attendanceCode}
                        </div>
                    );
                },
                width: isParentGroup ? '15%' : '25%'
            },
            {
                Header: '受講コース',
                accessor: 'ticketName',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.ticketName}
                        </div>
                    );
                },
                width: '15%'
            },
            {
                Header: '制限人数',
                accessor: 'limit',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.limit}
                        </div>
                    );
                },
                width: isParentGroup ? '10%' : '15%'
            },
            {
                Header: '有効期限',
                accessor: 'expirationDate',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            { original?.expirationDate?.replace(/-/g, '/')}
                        </div>
                    );
                },
                width: isParentGroup ? '10%' : '15%'
            },
            {
                // 確認中 learners (status 2) are excluded from 受講者数 since #18022 and counted here
                // instead. Shown for every group (email confirmation happens regardless of the approval
                // service). A code with nobody confirming shows a plain '0' (no link to an empty list).
                Header: '確認中数',
                accessor: 'numberConfirming',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const count = original?.numberConfirming ?? 0;
                    return (
                        <div>
                            {count > 0
                                ? <Link to={`/confirming-by-attendance-code/${original.id}`}>{count}</Link>
                                : count}
                        </div>
                    );
                },
                width: '10%'
            },
            {
                Header: '受講者数',
                accessor: 'numberUsed',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            <Link to={`/student-list-by-attendance-code/${original.id}`}>{ original?.numberUsed }</Link>
                        </div>
                    );
                },
                width: isParentGroup ? '10%' : '10%'
            }
        ]

        // Insert 承認待ち right before 受講者数, only for groups with the approval feature enabled.
        // A code with no pending learners shows a plain '0' (no link to the pending list).
        if (isAllowApprove) {
            const usedIdx = defaultColumns.findIndex(c => c.accessor === 'numberUsed');
            defaultColumns.splice(usedIdx, 0, {
                Header: '承認待ち数',
                accessor: 'numberPendingApproval',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const count = original?.numberPendingApproval ?? 0;
                    return (
                        <div>
                            {count > 0
                                ? <Link to={`/pending-approval-by-attendance-code/${original.id}`}>{count}</Link>
                                : count}
                        </div>
                    );
                },
                width: '10%'
            })
        }

        if(isParentGroup) {
            defaultColumns.splice(1, 0, {
                Header: '法人名',
                accessor: 'groupName',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.group?.name}
                        </div>
                    );
                },
                width: '20%'
            })
        }

        return defaultColumns;
    }, [userInfoHeader?.groups?.length, userInfoHeader?.isAllowApproveAttendanceCode])

    const companyAttendanceColumns = useMemo(() => {
        const isParentGroup = userInfoHeader?.groups?.length > 1;

        const defaultColumns = [
            {
                Header: '受講コード',
                accessor: 'attendanceCode',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>{original?.attendanceCode}</div>
                    );
                },
                width: isParentGroup ? '15%' : '20%'
            },
            {
                Header: '受講コース',
                accessor: 'ticketName',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.ticketName}
                        </div>
                    );
                },
                width: '17.5%'
            },
            {
                Header: '消化率50%未満',
                accessor: 'lt50',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {
                                original?.lt50 != null ? (
                                    <Link 
                                        to={`/student-list-by-attendance-list-code/${original.groupAttendanceId}`} 
                                        state={{ attendanceCode: original?.attendanceCode, bucket: 'lt50' }}>
                                        {original?.lt50}
                                    </Link>
                                ) : '--'
                            }
                            
                        </div>
                    );
                },
                width: isParentGroup ? '15%' : '18.5%'
            },
            {
                Header: '消化率50%以上100%未満',
                accessor: 'gte50',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {
                                original?.gte50 != null ? (
                                    <Link
                                        to={`/student-list-by-attendance-list-code/${original.groupAttendanceId}`}
                                        state={{ attendanceCode: original?.attendanceCode, bucket: 'gte50' }}>
                                        {original?.gte50}
                                    </Link>
                                ) : '--'
                            }
                        </div>
                    );
                },
                width: isParentGroup ? '15%' : '18.5%'
            },
            {
                Header: '消化率100%',
                accessor: 'eq100',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {
                                original?.eq100 != null ? (
                                    <Link
                                        to={`/student-list-by-attendance-list-code/${original.groupAttendanceId}`}
                                        state={{ attendanceCode: original?.attendanceCode, bucket: 'eq100' }}>
                                        {original?.eq100}
                                    </Link>
                                ) : '--'
                            }
                        </div>
                    );
                },
                width: isParentGroup ? '15%' : '18.5%'
            }
        ]

        if(isParentGroup) {
            defaultColumns.splice(1, 0, {
                Header: '法人名',
                accessor: 'groupName',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return (
                        <div>
                            {original?.group?.name}
                        </div>
                    );
                },
                width: '20%'
            })
        }

        return defaultColumns;
    }, [userInfoHeader?.groups?.length])

    // #18003 - the figures of this table are produced by a batch, not read live: show the GA when
    // they were last refreshed so a number that lags behind reality is not read as a bug.
    const completeRateUpdatedAt = useMemo(() => {
        const updatedAt = companyAttendanceRateList?.lastUpdatedAt;
        if (!updatedAt) return null;
        const parsed = moment(updatedAt).tz('Asia/Tokyo');
        return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm') : null;
    }, [companyAttendanceRateList?.lastUpdatedAt])

    return(
        <>
            <div id='main' className='wrapper main-student-list main-view-student-detail home-page-group-admin'>
                <section className='Banner-Home'>
                    {
                        lstInformation?.length > 0 && (
                            <div className='carousel-information d-flex align-items-center bg-gray'>
                                <div className='carousel-information-title'>
                                    <span style={{ color: '#FFB900' }} className=''>Pharma Info</span>
                                    <div
                                        className='pd-lr-20 view-lst-information-hyperlink-title'
                                        onClick={() => navigate(SYSTEM_PATH.INFORMATION_SCREEN)}
                                    ><span className='hyperlink'>一覧をみる</span>{'>'}{'>'}</div>
                                </div>
                                <VerticalCarousel
                                    className='height-40'
                                    slides={formatDataCarouselInformation}
                                    useTooltipHover
                                    allowTouchMove={false}
                                    onHandleNavigateDetailSlide={onHandleNavigateDetailSlide}
                                />
                                <div
                                    className='pd-lr-20 view-lst-information-hyperlink'
                                    onClick={() => navigate(SYSTEM_PATH.INFORMATION_SCREEN)}
                                ><span className='hyperlink'>一覧をみる</span>{'>'}{'>'}</div>
                            </div>
                        )
                    }
                    <NotifyList/>
                </section>

                <section className='home-statistical'>
                    {/* <div className='container d-flex justify-content-between align-items-center flex-wrap'>
                        <h2 className='title-primary mg-b-0'>【ダッシュボード】</h2>
                    </div> */}
                    <section className='content-student-list'>
                        <FirstTest />
                    </section>

                    {!isDepartmentFilter && (
                        <>
                            <section className='content-student-list content-view-student-detail pd-t-20'>
                                <div className='container'>
                                    <div className='d-flex align-items-center justify-content-between flex-wrap'>
                                        <h1 className='title-primary'>受講コード別の受講者数</h1>
                                        <button
                                            className='btn-export-csv'
                                            onClick={onExportAttendanceStudentList}
                                            disabled={!attendancesOfCompany?.elements?.length}
                                        >
                                            <i className='fa-solid fa-download mg-r-5'></i>
                                            CSVダウンロード
                                        </button>
                                    </div>
                                    {loadingAttendancesOfCompany && !attendancesOfCompany?.elements ? (
                                        // +1: the select-row checkbox column the table adds itself
                                        <TableSkeleton rows={5} columns={attendanceColumns.length + 1} className='mg-t-35' />
                                    ) : (
                                        <div className={classNames('dashboard-table', loadingAttendancesOfCompany && 'dashboard-table-refreshing')}>
                                            <Table
                                                columns={attendanceColumns}
                                                data={attendancesOfCompany.elements || []}
                                                enableServerSidePaging={true}
                                                initialTableState={attendancesOfCompany.paginate}
                                                onFetch={onGetAttendancesOfCompany}
                                                enableSelectRow={true}
                                                onSelectedChange={onAttendanceSelectedChange}
                                                selectedRowIds={selectedAttendanceRowIds}
                                                rowKey={'id'}
                                                disablePaging={false}
                                                disableSelectionPageSize={true}
                                                showPagingInfo={false}
                                                className='lst-attendance-table mg-t-35'
                                            />
                                        </div>
                                    )}
                                </div>
                            </section>

                            <section className='content-student-list content-view-student-detail content-view-student-detail-2'>
                                <div className='container'>
                                    <div className='d-flex align-items-center justify-content-between flex-wrap'>
                                        <h1 className='title-primary'>受講コード別コース消化率</h1>
                                        <button
                                            className='btn-export-csv'
                                            onClick={onExportAttendanceCompleteRate}
                                            disabled={!companyAttendanceRateList?.elements?.length}
                                        >
                                            <i className='fa-solid fa-download mg-r-5'></i>
                                            CSVダウンロード
                                        </button>
                                    </div>
                                    {/* Belongs to the table right below it, not to the CSV button above: it tells how
                                        fresh the figures in that table are. */}
                                    {completeRateUpdatedAt && (
                                        <div className='dashboard-table-updated-at'>
                                            最終更新：{completeRateUpdatedAt}
                                        </div>
                                    )}
                                    {loadingCompanyAttendanceRate && !companyAttendanceRateList?.elements ? (
                                        // +1: the select-row checkbox column the table adds itself
                                        <TableSkeleton
                                            rows={5}
                                            columns={companyAttendanceColumns.length + 1}
                                            className={completeRateUpdatedAt ? '' : 'mg-t-35'}
                                        />
                                    ) : (
                                        <div className={classNames('dashboard-table', loadingCompanyAttendanceRate && 'dashboard-table-refreshing')}>
                                            <Table
                                                columns={companyAttendanceColumns}
                                                data={companyAttendanceRateList.elements || []}
                                                enableServerSidePaging={true}
                                                initialTableState={companyAttendanceRateList.paginate}
                                                onFetch={onGetCompanyAttendanceRate}
                                                enableSelectRow={true}
                                                onSelectedChange={onCompleteRateSelectedChange}
                                                selectedRowIds={selectedCompleteRateRowIds}
                                                rowKey={'groupAttendanceId'}
                                                disablePaging={false}
                                                disableSelectionPageSize={true}
                                                showPagingInfo={false}
                                                className={classNames('lst-attendance-table', !completeRateUpdatedAt && 'mg-t-35')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </section>
                        </>
                    )}
                </section>            
            </div>
        </>

    )
})

export default GAHomeScreen;